import React, { useState } from 'react';
import styles from './GroupMemberRow.module.scss';
import classNames from 'classnames';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { getFullName } from '../../utils';
import Menu from '../atoms/Menu';
import ConfirmationDialog from '../DialogWrappers/ConfirmationDialog';
import { UserType } from '../../constants';

import type { Member } from '../../types';

interface GroupMemberRowProps {
  member: Member;
  openEditClientModal: () => void;
  onRemoveMember: () => void;
  onGoToInDepthUserPage: () => void;
  userType: UserType;
}

const GroupMemberRow: React.FC<GroupMemberRowProps> = ({
  userType,
  member,
  openEditClientModal,
  onRemoveMember,
  onGoToInDepthUserPage,
}) => {
  const [isRemoveMemberConfirmationOpen, setIsRemoveMemberConfirmationOpen] =
    useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleCloseMenuOptions = () => {
    setAnchorEl(null);
  };

  const handleEditClient = () => {
    openEditClientModal();
    handleCloseMenuOptions();
  };

  const handleRemoveMember = async () => {
    handleCloseMenuOptions();
    await onRemoveMember();
    setIsRemoveMemberConfirmationOpen(false);
  };

  const handleGoToInDepthUserPage = () => {
    onGoToInDepthUserPage();
    handleCloseMenuOptions();
  };

  const renderUserName = () => (
    <div
      className={classNames(styles.userColumn, styles.userName)}
      onClick={onGoToInDepthUserPage}
    >
      <AccountCircleOutlinedIcon className={styles.userIcon} color="primary" />
      {getFullName(member)}
    </div>
  );

  const renderEmail = () => (
    <div className={classNames(styles.userColumn, styles.email)}>
      {member.email}
    </div>
  );

  const renderProgram = () => (
    <div className={classNames(styles.userColumn, styles.program)}>
      {member.program?.name}
    </div>
  );

  const renderPin = () => (
    <div className={classNames(styles.userColumn, styles.pin)}>
      {member.pin}
    </div>
  );

  const handleRemoveMemberClick = () => {
    setIsRemoveMemberConfirmationOpen(true);
    handleCloseMenuOptions();
  };

  const renderRemoveMemberConfirmationDialog = () => (
    <ConfirmationDialog
      text={`Are you sure you want to remove this ${userType.toLowerCase()} from the group?`}
      onSubmit={handleRemoveMember}
      submitText="Yes"
      cancelText="No"
      isOpen={isRemoveMemberConfirmationOpen}
      onCancel={() => setIsRemoveMemberConfirmationOpen(false)}
      symbol={<DeleteForeverOutlinedIcon />}
    />
  );

  const getUserOptions = () => {
    return [
      {
        onClick: handleEditClient,
        label: `Edit Member Details`,
        icon: <BorderColorOutlinedIcon color="primary" />,
      },
      {
        onClick: handleGoToInDepthUserPage,
        label: `Go to In-Depth User Page`,
        icon: <TrendingUpIcon color="primary" />,
      },
      {
        onClick: handleRemoveMemberClick,
        label: `Remove Member`,
        icon: <DeleteForeverOutlinedIcon color="primary" />,
      },
    ];
  };

  const renderMoreOptions = () => (
    <div className={classNames(styles.userColumn, styles.options)}>
      <PendingOutlinedIcon
        // @ts-ignore
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget as unknown as HTMLElement);
        }}
        className={styles.optionsBtn}
        color="primary"
      />
      <Menu
        anchorEl={anchorEl}
        isOpen={open}
        onClose={handleCloseMenuOptions}
        options={getUserOptions()}
      />
    </div>
  );

  return (
    <div
      className={classNames(styles.userWorkoutsRow, styles.workoutsListGrid)}
    >
      {renderUserName()}
      {renderEmail()}
      {renderProgram()}
      {renderPin()}
      {renderMoreOptions()}
      {renderRemoveMemberConfirmationDialog()}
    </div>
  );
};

export default GroupMemberRow;
